import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { FilterControlsComponent } from '@cabinet/components/filter-controls/filter-controls.component';
import { ProjectFilterTypeService } from '@cabinet/services/project-filter-type.service';
import { ProjectModel } from '@core/models/project.model';
import { StatusModel } from '@core/models/status.model';
import { TagModel } from '@core/models/tag.model';
import { TaskModel } from '@core/models/task.model';
import { UserModel } from '@core/models/user.model';
import * as fromRoot from '@core/redux/index';
import * as TaskActions from '@core/redux/task/task.actions';
import { DragDropService } from '@core/services/drag-drop.service';
import { StoreTaskService } from '@core/services/store-task.service';
import { select, Store } from '@ngrx/store';
import { AvatarComponent } from '@ux/components/avatar/avatar.component';
import { GroupDropdownComponent } from '@ux/components/group-dropdown/group-dropdown.component';
import { GroupValue } from '@ux/components/group-dropdown/group-option.interface';
import { StatusComponent } from '@ux/components/status/status.component';
import { TagComponent } from '@ux/components/tag/tag.component';
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2';
import { filter } from 'rxjs/operators';

interface TaskListState {
  isShowBacklog: boolean;
  isLoading: boolean;
  isOpenCreateForm: boolean;
  items: TaskModel[];
  statuses: StatusModel[];
  model: TaskModel;
  openTaskId: string | null;
  backLogStatus: StatusModel | null;
}

// Local Storage key for saving group mode
const GROUP_MODE_STORAGE_KEY = 'itasks-group-mode';

interface IGroupedTasks {
  key: string;
  tasks: TaskModel[];
  name: string;
}

@Component({
  selector: 'app-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.less'],
  imports: [
    FilterControlsComponent,
    FormsModule,
    RouterModule,
    TagComponent,
    StatusComponent,
    AvatarComponent,
    GroupDropdownComponent,
    NgTemplateOutlet,
    NgClickOutsideDelayOutsideDirective,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskListComponent implements AfterViewChecked {
  private readonly taskService = inject(StoreTaskService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectFilterTypeService = inject(ProjectFilterTypeService);
  private readonly dragDropService = inject(DragDropService);

  @ViewChild('taskInput') taskInput: ElementRef;

  // Proxy to the service's isAllProject signal
  protected readonly isAllProject = this.projectFilterTypeService.isAllProject;

  private readonly state = signal<TaskListState>({
    isShowBacklog: false,
    isLoading: false,
    isOpenCreateForm: false,
    items: [],
    statuses: [],
    model: new TaskModel(),
    openTaskId: null,
    backLogStatus: null,
  });

  /** Grouping mode signal */
  readonly groupMode = signal<GroupValue>('none');
  /** Map of collapsed group states */
  readonly collapsedGroups = signal<Record<string, boolean>>({});

  readonly isShowBacklog = computed(() => this.state().isShowBacklog);
  readonly isLoading = computed(() => this.state().isLoading);
  readonly isOpenCreateForm = computed(() => this.state().isOpenCreateForm);
  readonly model = computed(() => this.state().model);
  readonly openTaskId = computed(() => this.state().openTaskId);

  readonly tasks = toSignal(this.taskService.getTaskList(), {
    initialValue: [] as TaskModel[],
  });

  readonly backlogStatus = toSignal(
    this.store.pipe(
      select(fromRoot.getStatusBackLog),
      filter((status): status is StatusModel => !!status)
    ),
    { initialValue: null as StatusModel | null }
  );

  readonly statuses = toSignal(this.store.pipe(select(fromRoot.getStatusEntities)), {
    initialValue: [] as StatusModel[],
  });

  readonly loading = toSignal(this.store.pipe(select(fromRoot.getTaskLoading)), { initialValue: false });

  readonly queryParams = toSignal(this.route.queryParams, {
    initialValue: {} as Record<string, string | string[]>,
  });

  readonly projects = toSignal(this.store.pipe(select(fromRoot.getProjectEntities)), {
    initialValue: [] as ProjectModel[],
  });

  readonly itemsShow$ = computed(() => {
    const tasks = this.tasks();
    const backlogStatus = this.backlogStatus();
    const showBacklog = this.isShowBacklog();

    if (!tasks) {
      return [];
    }

    // Функция для получения времени последнего обновления
    const getLastUpdateTime = (task: TaskModel): number => {
      const updateTime = task.updatedAt
        ? new Date(task.updatedAt).getTime()
        : task.createdAt
          ? new Date(task.createdAt).getTime()
          : 0;
      return updateTime;
    };

    // Сортировка по времени обновления
    const sortByUpdateTime = (a: TaskModel, b: TaskModel): number => getLastUpdateTime(b) - getLastUpdateTime(a);

    if (showBacklog) {
      // Если беклог включен, сортируем все задачи по времени обновления
      return tasks.sort(sortByUpdateTime);
    } else {
      // Если беклог выключен, фильтруем задачи без беклога и сортируем по времени
      return backlogStatus?.id
        ? tasks.filter(item => item.statusId !== backlogStatus?.id).sort(sortByUpdateTime)
        : tasks.sort(sortByUpdateTime);
    }
  });

  // Computed signal for grouped tasks
  readonly groupedTasks = computed(() => {
    if (this.groupMode() === 'none') {
      return [];
    }

    if (this.groupMode() === 'tag') {
      return this.getGroupByTag();
    }

    if (this.groupMode() === 'status') {
      return this.getGroupByStatus();
    }

    if (this.groupMode() === 'createdAt') {
      return this.getGroupByDate('createdAt');
    }

    if (this.groupMode() === 'updatedAt') {
      return this.getGroupByDate('updatedAt');
    }

    if (this.groupMode() === 'responsible') {
      return this.getGroupByResponsible();
    }

    if (this.groupMode() === 'deadlineAt') {
      return this.getGroupByDeadline();
    }

    return [];
  });

  @Output() particleLoad = new EventEmitter<void>();

  private focusSet = false;
  private isDragging = false;

  constructor() {
    this.store
      .pipe(
        select(fromRoot.getStatusBackLog),
        filter((status): status is StatusModel => !!status),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(status => {
        this.state.update(s => ({ ...s, backLogStatus: status }));
      });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const model = new TaskModel();

      if (params['responsibleId']) {
        model.setAttributes({
          responsibleId: Array.isArray(params['responsibleId']) ? params['responsibleId'][0] : params['responsibleId'],
        });
      }

      if (params['statusId']) {
        model.setAttributes({
          statusId: Array.isArray(params['statusId']) ? params['statusId'][0] : params['statusId'],
        });
      }

      this.state.update(s => ({
        ...s,
        model: Object.assign(new TaskModel(), s.model, {
          name: s.model.name,
          statusId: model.statusId,
          responsibleId: model.responsibleId,
        }),
        openTaskId: params['id'] || null,
      }));
    });

    this.loadGroupModeFromStorage();
  }

  ngAfterViewChecked() {
    if (this.taskInput && this.isOpenCreateForm() && !this.focusSet) {
      this.taskInput.nativeElement.focus();
      this.focusSet = true;
    }

    if (!this.isOpenCreateForm()) {
      this.focusSet = false;
    }
  }

  onToggleGroupMode(mode: GroupValue) {
    this.groupMode.set(mode);
    this.collapsedGroups.set({});

    // Сохраняем в localStorage только при изменении группировки
    this.saveGroupModeToStorage(mode);
  }

  /**
   * Save group mode to localStorage
   * @param mode Group mode to save
   */
  private saveGroupModeToStorage(mode: string) {
    try {
      localStorage.setItem(GROUP_MODE_STORAGE_KEY, mode);
    } catch (error) {
      console.error('Error saving group mode to localStorage:', error);
    }
  }

  /**
   * Load group mode from localStorage
   */
  private loadGroupModeFromStorage() {
    try {
      const savedMode = localStorage.getItem(GROUP_MODE_STORAGE_KEY);
      if (savedMode) {
        this.groupMode.set(savedMode as GroupValue);
      }
    } catch (error) {
      console.error('Error loading group mode from localStorage:', error);
    }
  }

  onToggleGroup(groupKey: string) {
    this.collapsedGroups.update(groups => ({
      ...groups,
      [groupKey]: !groups[groupKey],
    }));
  }

  private createGroup(key: string, name: string): IGroupedTasks {
    return {
      key: key,
      name: name,
      tasks: [],
    };
  }

  private getGroupByTag(): IGroupedTasks[] {
    const groups: { [key: string]: IGroupedTasks } = {};

    this.itemsShow$().forEach((task: TaskModel) => {
      if (task.tagList) {
        task.tagList.forEach((tag: TagModel) => {
          if (!groups[tag.name]) {
            groups[tag.name] = this.createGroup(tag.name, tag.name);
          }
          groups[tag.name].tasks.push(task);
        });
      }
    });

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map(key => groups[key]);
  }

  private getGroupByStatus(): IGroupedTasks[] {
    const groups: { [key: string]: IGroupedTasks } = {};
    const statusMap = new Map<string, number>();
    this.statuses().forEach((status, index) => {
      statusMap.set(status.name, index);
    });

    this.itemsShow$().forEach((task: TaskModel) => {
      const status = this.statuses().find(s => s.id === task.statusId);
      if (status) {
        if (!groups[status.name]) {
          groups[status.name] = this.createGroup(status.name, status.name);
        }
        groups[status.name].tasks.push(task);
      }
    });

    return Object.keys(groups)
      .sort(
        (a, b) =>
          // Sort by original status order from store
          (statusMap.get(a) || 0) - (statusMap.get(b) || 0)
      )
      .map(key => groups[key]);
  }

  private formatDate(date: string): string {
    const d = new Date(date);
    return d.toLocaleDateString();
  }

  private getGroupByDate(dateField: 'createdAt' | 'updatedAt'): IGroupedTasks[] {
    const groups: { [key: string]: IGroupedTasks } = {};

    this.itemsShow$().forEach((task: TaskModel) => {
      const dateStr = this.formatDate(task[dateField]);
      if (!groups[dateStr]) {
        groups[dateStr] = this.createGroup(dateStr, dateStr);
      }
      groups[dateStr].tasks.push(task);
    });

    return Object.keys(groups)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map(key => groups[key]);
  }

  private getGroupByResponsible(): IGroupedTasks[] {
    const groups: { [key: string]: IGroupedTasks } = {};
    const noResponsibleKey = 'no-responsible';

    this.itemsShow$().forEach((task: TaskModel) => {
      if (task.responsible) {
        const key = task.responsible.userId;
        if (!groups[key]) {
          groups[key] = this.createGroup(key, `${task.responsible.user.firstName} ${task.responsible.user.lastName}`);
        }
        groups[key].tasks.push(task);
      } else {
        if (!groups[noResponsibleKey]) {
          groups[noResponsibleKey] = this.createGroup(noResponsibleKey, 'Без исполнителя');
        }
        groups[noResponsibleKey].tasks.push(task);
      }
    });

    return Object.keys(groups)
      .sort((a, b) => {
        if (a === noResponsibleKey) {
          return 1;
        }
        if (b === noResponsibleKey) {
          return -1;
        }
        return groups[a].name.localeCompare(groups[b].name);
      })
      .map(key => groups[key]);
  }

  private getGroupByDeadline(): IGroupedTasks[] {
    const groups: { [key: string]: IGroupedTasks } = {
      'no-deadline': this.createGroup('no-deadline', 'Без дедлайна'),
      overdue: this.createGroup('overdue', 'Просроченные'),
      today: this.createGroup('today', 'Сегодня'),
      tomorrow: this.createGroup('tomorrow', 'Завтра'),
      future: this.createGroup('future', 'В будущем'),
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const futureThreshold = new Date(today);
    futureThreshold.setDate(futureThreshold.getDate() + 14); // Две недели вперед

    this.itemsShow$().forEach((task: TaskModel) => {
      if (!task.deadlineAt) {
        groups['no-deadline'].tasks.push(task);
        return;
      }

      const deadline = new Date(task.deadlineAt);
      deadline.setHours(0, 0, 0, 0);

      if (deadline < today) {
        groups['overdue'].tasks.push(task);
      } else if (deadline.getTime() === today.getTime()) {
        groups['today'].tasks.push(task);
      } else if (deadline.getTime() === tomorrow.getTime()) {
        groups['tomorrow'].tasks.push(task);
      } else if (deadline < futureThreshold) {
        // Создаем группу для конкретной даты
        const dateKey = deadline.toISOString().split('T')[0];
        if (!groups[dateKey]) {
          const dateOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
          const weekdayOptions: Intl.DateTimeFormatOptions = { weekday: 'short' };

          const dateLabel = deadline.toLocaleDateString('ru-RU', dateOptions);
          const weekday = deadline
            .toLocaleDateString('ru-RU', weekdayOptions)
            .replace('.', '') // Убираем точку после сокращения
            .toLowerCase();

          groups[dateKey] = this.createGroup(dateKey, `${dateLabel} (${weekday})`);
        }
        groups[dateKey].tasks.push(task);
      } else {
        groups['future'].tasks.push(task);
      }
    });

    // Сортируем группы по дате
    const sortedGroups = Object.entries(groups)
      .filter(([key, group]) => group.tasks.length > 0)
      .sort(([keyA], [keyB]) => {
        // Специальные группы всегда в определенном порядке
        const specialOrder = {
          overdue: -4,
          today: -3,
          tomorrow: -2,
          future: 998,
          'no-deadline': 999,
        };

        const orderA = specialOrder[keyA];
        const orderB = specialOrder[keyB];

        // Если обе группы специальные
        if (orderA !== undefined && orderB !== undefined) {
          return orderA - orderB;
        }

        // Если только одна группа специальная
        if (orderA !== undefined) {
          return orderA < 0 ? -1 : 1; // Отрицательные в начало, положительные в конец
        }
        if (orderB !== undefined) {
          return orderB < 0 ? 1 : -1; // Отрицательные в начало, положительные в конец
        }

        // Если обе группы - даты, сортируем по дате
        return keyA.localeCompare(keyB);
      })
      .map(([, group]) => group);

    return sortedGroups;
  }

  isGroupCollapsed(groupKey: string): boolean {
    return this.collapsedGroups()[groupKey] === true;
  }

  onUpdateModelName(name: string): void {
    this.state.update(s => ({
      ...s,
      model: Object.assign(new TaskModel(), s.model, { name }),
    }));
  }

  onCreate(): void {
    const currentState = this.state();
    if (!this.loading() && currentState.model.name) {
      const model = new TaskModel();
      model.setAttributes({
        ...currentState.model,
        statusId: currentState.model.statusId || this.statuses()[1].id,
      });

      this.state.update(s => ({
        ...s,
        isShowBacklog: true,
        model: new TaskModel().setAttributes({
          responsibleId: s.model.responsibleId,
          statusId: s.model.statusId,
        }),
      }));

      this.store.dispatch(new TaskActions.CreateAction(model));
    }
  }

  onToggleCreateForm(): void {
    this.state.update(s => ({
      ...s,
      isOpenCreateForm: !s.isOpenCreateForm,
    }));
  }

  onFilterByUser(user: UserModel | null, event: Event): void {
    event.stopPropagation();
    if (user) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { responsibleId: user.id },
        queryParamsHandling: 'merge',
      });
    }
  }

  onFilterByTag(tag: TagModel, event: Event): void {
    event.stopPropagation();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tagId: tag.id },
      queryParamsHandling: 'merge',
    });
  }

  onFilterByStatus(status: StatusModel, event: Event): void {
    event.stopPropagation();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { statusId: status.id },
      queryParamsHandling: 'merge',
    });
  }

  onToggleBacklog(): void {
    this.state.update(s => ({
      ...s,
      isShowBacklog: !s.isShowBacklog,
    }));
  }

  onDragStart(event: DragEvent, taskModel: TaskModel): void {
    this.isDragging = true;
    this.dragDropService.startDrag(taskModel, event.target as HTMLElement);

    // Устанавливаем данные для drag & drop
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', taskModel.id);
    }
  }

  onDragEnd(event: DragEvent): void {
    this.dragDropService.endDrag();
    // Сбрасываем флаг с небольшой задержкой, чтобы предотвратить клик
    setTimeout(() => {
      this.isDragging = false;
    }, 100);
  }

  onCloseCreateForm(): void {
    this.state.update(s => ({
      ...s,
      isOpenCreateForm: false,
    }));
  }

  onTaskClick(taskModel: TaskModel, event: Event): void {
    // Предотвращаем клик если это был drag
    if (this.isDragging || this.dragDropService.isCurrentlyDragging()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { id: taskModel.id },
      queryParamsHandling: 'merge',
    });
  }
}
